import { createTheme } from '@mui/material/styles';
import { darken, lighten } from '@mui/material/';
import { Colors } from 'src/shared/components/styles/colors';
import { svSE } from '@mui/material/locale';
import { svSE as datagridsvSE } from '@mui/x-data-grid';
import { svSE as pickersvSE } from '@mui/x-date-pickers/locales';
import { grey } from '@mui/material/colors';
import shadows from '@mui/material/styles/shadows';

const theme = createTheme(
  {
    typography: {
      fontFamily: '"Source Sans 3"',
    },
    palette: {
      background: {
        default: Colors.PageBackground,
      },
      primary: {
        main: Colors.Blue,
      },
      secondary: {
        main: Colors.Secondary.Main,
        light: lighten(Colors.Secondary.Main, 0.2),
        dark: darken(Colors.Secondary.Main, 0.2),
        contrastText: Colors.White,
      },
      error: {
        main: Colors.ChipColors.Red,
        dark: darken(Colors.ChipColors.Red, 0.2),
        light: lighten(Colors.ChipColors.Red, 0.2),
        contrastText: Colors.White,
      },
      warning: {
        main: Colors.ChipColors.Yellow,
        dark: darken(Colors.ChipColors.Yellow, 0.2),
        light: lighten(Colors.ChipColors.Yellow, 0.2),
        contrastText: Colors.White,
      },
      success: {
        main: Colors.ChipColors.Green,
        dark: darken(Colors.ChipColors.Green, 0.2),
        light: lighten(Colors.ChipColors.Green, 0.2),
        contrastText: Colors.White,
      },
      info: {
        main: Colors.ChipColors.Blue,
        dark: darken(Colors.ChipColors.Blue, 0.2),
        light: lighten(Colors.ChipColors.Blue, 0.2),
        contrastText: Colors.White,
      },
    },
    components: {
      MuiPaper: {
        // "Hairline border"-stil: synlig 1px border + minimal skugga + 8 px hörn.
        // Matchar moderna list/tabell-dashboards där bordern definierar gränsen
        // och skuggan är så subtil att den knappt syns.
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            borderRadius: '8px',
            border: '1px solid #dadde1',
            backgroundImage: 'none',
            // MUI-komponenter som använder Paper internt — behåll deras defaults
            '&.MuiAppBar-root, &.MuiDrawer-paper': {
              borderRadius: 0,
              border: 'none',
            },
            '&.MuiDrawer-paper': {
              boxShadow: 'none',
            },
            '&.MuiAccordion-root': {
              borderRadius: 0,
              boxShadow: 'none',
              border: 'none',
            },
            // Alert: 4 px-radius och hairline-border i severity-färg så alla fritt liggande Alerts
            // får samma konsekventa avgränsning (matchar dashboard-editor-mönstret globalt).
            '&.MuiAlert-root': {
              borderRadius: '4px',
              borderWidth: '1px',
              borderStyle: 'solid',
              '&.MuiAlert-standardInfo, &.MuiAlert-outlinedInfo, &.MuiAlert-filledInfo': {
                borderColor: Colors.ChipColors.Blue,
              },
              '&.MuiAlert-standardSuccess, &.MuiAlert-outlinedSuccess, &.MuiAlert-filledSuccess': {
                borderColor: Colors.ChipColors.Green,
              },
              '&.MuiAlert-standardWarning, &.MuiAlert-outlinedWarning, &.MuiAlert-filledWarning': {
                borderColor: Colors.ChipColors.Yellow,
              },
              '&.MuiAlert-standardError, &.MuiAlert-outlinedError, &.MuiAlert-filledError': {
                borderColor: Colors.ChipColors.Red,
              },
            },
            // Outlined-varianten används som fältgrupp/input-lik container — matcha inputs (4 px) istället för cards (8 px)
            '&.MuiPaper-outlined': {
              borderRadius: '4px',
            },
            // Alerts nestade inuti ett Paper (card, dialog, modal etc.) ska INTE ha den färgade
            // borden — containern avgränsar redan. Endast fritt liggande Alerts på sidnivå behåller borden.
            // !important säkrar att den vinner över &.MuiAlert-root och per-severity-reglerna ovan.
            '& .MuiAlert-root': {
              border: 'none !important',
            },
            // Nestade Paper (Paper inuti Paper) ska inte få sin egen "card"-styling — de
            // ärver ytan från yttre containern. Annars får detaljvyer dubbla borders.
            // Undantag: variant="outlined" — egen border. :where() sänker specificitet till 0
            // så att individuella komponenter (ColoredBox m.fl.) kan vinna med sx-värden.
            '& :where(.MuiPaper-root:not(.MuiAppBar-root):not(.MuiMenu-paper):not(.MuiPopover-paper):not(.MuiDialog-paper):not(.MuiDrawer-paper):not(.MuiAccordion-root):not(.MuiSnackbarContent-root):not(.MuiAlert-root):not(.MuiPaper-outlined))':
              {
                border: 'none',
                borderRadius: 0,
                boxShadow: 'none',
              },
            // Card är en styled Paper men har egen .MuiCard-root klass. Vi vill att Cards
            // som är nestade i Paper (t.ex. ContentSection) tappar sin egen border/radius/shadow.
            // Top-level Card (utan Paper-parent) träffas inte av denna selector.
            // Undantag: variant="outlined" (FormFieldset m.fl.) — egen border ska behållas.
            '& .MuiCard-root:not(.MuiPaper-outlined)': {
              border: 'none',
              borderRadius: 0,
              boxShadow: 'none',
            },
          },
          elevation0: {
            boxShadow: '0 1px 2px rgba(20,18,15,0.04)',
          },
        },
      },
      MuiCard: {
        // Top-level Card behåller Paper-stilen (border, radius, shadow ärvs via MuiPaper-overriden).
        // Nestade Card (t.ex. ContentSection inom yttre Paper) plattas via Paper-regelns
        // descendant-selector nedan så vi inte får dubbla card-kanter.
        defaultProps: {
          elevation: 0,
        },
      },
      MuiButton: {
        // Kompaktare knapphöjd än MUI default (~36.5 px) för en modernare dashboard-känsla.
        // Sänker vertikal padding: medium ~32 px, small ~28 px, large ~38 px.
        styleOverrides: {
          sizeSmall: {
            paddingTop: 2,
            paddingBottom: 2,
          },
          sizeMedium: {
            paddingTop: 4,
            paddingBottom: 4,
          },
          sizeLarge: {
            paddingTop: 6,
            paddingBottom: 6,
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          input: {
            '&::placeholder': {
              color: `${grey[500]} !important`,
              fontWeight: `100 !important`,
            },
          },
          a: {
            color: Colors.LinkColor,
          },
          '.section-inset': {
            boxShadow: '0 6px 13px -13px rgba(0,0,0,0.4) inset !important',
          },
          '.Toastify__toast': {
            boxShadow: shadows[10],
          },
          '.mui-toast': {
            padding: '0 8px 0 0',
            '& .Toastify__toast-body': {
              padding: 0,
            },
            '& .Toastify__close-button': {
              color: grey[800],
              marginTop: 8,
              '& > svg': {
                width: 20,
                height: 20,
              },
            },
            '&.member-export': {
              width: 500,
            },
          },
          body: {
            backgroundColor: Colors.PageBackground,
            margin: '0rem !important',
            // scrollbarColor: 'red blue',
            '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
              backgroundColor: 'transparent',
              width: '8px !important',
            },
            '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
              borderRadius: 8,
              backgroundColor: 'rgba(0, 0 ,0 , 0.3)',
              minHeight: 24,
              width: '8px !important',
            },
            '&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus': {
              backgroundColor: 'rgba(0, 0 ,0 , 0.5)',
            },
            '&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active': {
              backgroundColor: 'rgba(0, 0 ,0 , 0.5)',
            },
            '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
              backgroundColor: 'rgba(0, 0 ,0 , 0.5)',
            },
            '&::-webkit-scrollbar-corner, & *::-webkit-scrollbar-corner': {
              backgroundColor: 'transparent',
            },
            // Mörk backdrop endast för "modala" overlays (Dialog, Drawer, Modal). Menu/Popover sätter
            // BackdropProps.invisible=true själva — exkludera dem så sidan inte mörknar vid t.ex. dropdown-meny.
            '& .MuiBackdrop-root:not(.MuiBackdrop-invisible)': {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
            },
            '& .MuiPaper-outlined': {
              border: '1px solid rgb(196, 196, 196)',
            },
            '.MuiDataGrid-row:nth-of-type(2n+1)': {
              backgroundColor: 'rgba(0,0,0,0.015)',
            },
          },
        },
      },
    },
  },
  pickersvSE,
  datagridsvSE,
  svSE,
);

export default theme;
